import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { ToolGate } from '../../components/ToolGate';
import { Button, Alert, Loading, EmptyState, Badge } from '../../components/ui';
import { ContactRow } from '../../lib/types';

// ── API response shapes ──────────────────────────────────────────────────────

interface ContactsResponse {
  contacts: ContactRow[];
}

interface GenerateResponse {
  subject: string;
  body: string;
}

interface LogResponse {
  ok: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Email() {
  // Contact list state
  const [search, setSearch] = useState<string>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Email draft state
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');

  // Action state
  const [generating, setGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [sentSuccess, setSentSuccess] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState<string | null>(null);
  const [logging, setLogging] = useState<boolean>(false);

  // Fetch contacts
  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
  } = useAsync<ContactsResponse>(() => apiGet('/api/contacts?limit=200'), []);

  const contacts: ContactRow[] = useMemo(
    () => (contactsData?.contacts ?? []).filter((c) => c.email),
    [contactsData],
  );

  const filtered: ContactRow[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.businessName?.toLowerCase().includes(q) ||
        c.contactName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q),
    );
  }, [contacts, search]);

  const selected: ContactRow | undefined = contacts.find((c) => c.id === selectedId);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSelectContact(contact: ContactRow) {
    setSelectedId(contact.id);
    setSubject('');
    setBody('');
    setGenerateError(null);
    setLogError(null);
    setSentSuccess(null);
    setReplySuccess(null);
    setCopied(false);
  }

  async function handleGenerate() {
    if (!selectedId) return;
    setGenerating(true);
    setGenerateError(null);
    setSentSuccess(null);
    setReplySuccess(null);
    setLogError(null);

    try {
      const res = await apiPost<GenerateResponse>('/api/tools/email/generate', {
        contactId: selectedId,
      });
      setSubject(res.subject ?? '');
      setBody(res.body ?? '');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setGenerateError(err.message || 'AI is not configured. Contact your admin.');
      } else if (err instanceof ApiError) {
        setGenerateError(err.message);
      } else if (err instanceof Error) {
        setGenerateError(err.message);
      } else {
        setGenerateError('Something went wrong generating the email.');
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleCopy() {
    const text = `${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available — silently ignore
    }
  }

  async function handleLog(kind: 'sent' | 'reply') {
    if (!selectedId) return;
    setLogging(true);
    setLogError(null);
    setSentSuccess(null);
    setReplySuccess(null);

    try {
      await apiPost<LogResponse>('/api/tools/email/log', {
        contactId: selectedId,
        kind,
      });
      if (kind === 'sent') {
        setSentSuccess('Marked as sent.');
      } else {
        setReplySuccess('Reply logged.');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setLogError(err.message || 'This contact has opted out and cannot be emailed.');
      } else if (err instanceof ApiError) {
        setLogError(err.message);
      } else if (err instanceof Error) {
        setLogError(err.message);
      } else {
        setLogError('Something went wrong logging the action.');
      }
    } finally {
      setLogging(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <ToolGate tool="email" title="Email">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-10">

        {/* Heading */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="h-display text-3xl text-soft">Email</h1>
            <p className="mt-1 text-sm text-muted">
              AI writes a cold email for what you actually sell. Edit it, copy it, send it yourself, mark it sent.
            </p>
          </div>
          <Link to="/contacts?import=1" className="shrink-0 text-sm font-semibold text-electric hover:underline">Import CSV →</Link>
        </div>

        {/* Two-pane layout */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">

          {/* LEFT — contact list */}
          <aside className="w-full lg:w-72 xl:w-80 shrink-0">
            <div className="card p-4 space-y-3">
              <h2 className="font-head text-sm font-semibold text-soft/80 uppercase tracking-wide">
                Contacts
              </h2>

              <input
                className="input"
                type="search"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search contacts"
              />

              {contactsLoading && <Loading label="Loading contacts…" />}

              {contactsError && (
                <Alert kind="error">{contactsError}</Alert>
              )}

              {!contactsLoading && !contactsError && contacts.length === 0 && (
                <EmptyState
                  title="No contacts yet"
                  body="Add some in the CRM first."
                />
              )}

              {!contactsLoading && !contactsError && contacts.length > 0 && (
                <ul className="max-h-[60vh] overflow-y-auto space-y-1 -mx-1 px-1">
                  {filtered.length === 0 ? (
                    <li className="py-6 text-center text-sm text-muted">No results</li>
                  ) : (
                    filtered.map((contact) => {
                      const isSelected = contact.id === selectedId;
                      return (
                        <li key={contact.id}>
                          <button
                            onClick={() => handleSelectContact(contact)}
                            className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                              isSelected
                                ? 'bg-electric/15 border border-electric/40'
                                : 'hover:bg-surface-3 border border-transparent'
                            }`}
                          >
                            <p className="truncate text-sm font-semibold text-soft leading-tight">
                              {contact.businessName || contact.contactName || '—'}
                            </p>
                            <p className="truncate text-xs text-muted mt-0.5">
                              {contact.email}
                            </p>
                            {contact.tier && (
                              <Badge className="mt-1 border-line-strong bg-surface-3 text-muted">
                                {contact.tier}
                              </Badge>
                            )}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          </aside>

          {/* RIGHT — email editor */}
          <section className="flex-1 min-w-0">
            {!selected ? (
              <EmptyState
                title="Select a contact"
                body="Pick a contact on the left to generate a personalised cold email."
              />
            ) : (
              <div className="card p-5 space-y-5">

                {/* Contact header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="h-display text-xl text-soft truncate">
                      {selected.businessName || selected.contactName || '—'}
                    </h2>
                    {selected.contactName && selected.businessName && (
                      <p className="text-sm text-muted">{selected.contactName}</p>
                    )}
                    <p className="text-sm text-electric">{selected.email}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="primary"
                      loading={generating}
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {subject || body ? 'Regenerate' : 'Generate email'}
                    </Button>
                    {subject || body ? (
                      <Button
                        variant="ghost"
                        onClick={handleGenerate}
                        disabled={generating}
                        className="hidden"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                </div>

                {generateError && <Alert kind="error">{generateError}</Alert>}

                {/* Draft editor */}
                {(subject || body) && (
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1.5 font-head text-sm font-semibold text-soft/90">
                        Subject
                      </label>
                      <input
                        className="input w-full"
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Email subject…"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 font-head text-sm font-semibold text-soft/90">
                        Body
                      </label>
                      <textarea
                        className="input w-full resize-y font-mono text-sm leading-relaxed"
                        style={{ minHeight: '220px' }}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Email body…"
                      />
                    </div>

                    {/* Action row */}
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      <Button
                        variant="primary"
                        loading={logging}
                        onClick={() => handleLog('sent')}
                        disabled={logging}
                      >
                        Mark as sent
                      </Button>

                      <Button
                        variant="ghost"
                        onClick={handleCopy}
                        disabled={generating}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>

                      <Button
                        variant="ghost"
                        loading={logging}
                        onClick={() => handleLog('reply')}
                        disabled={logging}
                      >
                        Log a reply
                      </Button>

                      <Button
                        variant="ghost"
                        onClick={handleGenerate}
                        disabled={generating}
                      >
                        Regenerate
                      </Button>
                    </div>

                    {logError && <Alert kind="error">{logError}</Alert>}
                    {sentSuccess && <Alert kind="success">{sentSuccess}</Alert>}
                    {replySuccess && <Alert kind="success">{replySuccess}</Alert>}
                  </div>
                )}

                {/* Prompt to generate when no draft yet */}
                {!subject && !body && !generating && !generateError && (
                  <p className="text-sm text-muted">
                    Hit "Generate email" to let AI draft a cold email for this contact.
                  </p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </ToolGate>
  );
}
